import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Megaphone, Sparkles, MapPin, Route as RouteIcon, Store, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { US_STATES } from "@/lib/usStates";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function AdvertiserSignup() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({ annual: 99, promo: { active: false } });
  const [f, setF] = useState({ business_name: "", address: "", city: "", state: "", zip: "", name: "", email: "", password: "", lat: null, lng: null, plan: "advertiser_annual" });
  const [locating, setLocating] = useState(false);
  const [territory, setTerritory] = useState(null); // {found, zone_name, bike_path_name, partner_shop_name}
  const [resolving, setResolving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [cov, setCov] = useState({ business_name: "", contact_name: "", email: "", phone: "", note: "" });
  const [covLoading, setCovLoading] = useState(false);
  const [covSent, setCovSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setC = (k) => (e) => setCov({ ...cov, [k]: e.target.value });

  const openCoverage = () => {
    setCov({ business_name: f.business_name, contact_name: f.name, email: f.email, phone: "", note: "" });
    setCovSent(false);
    setCoverageOpen(true);
  };
  const submitCoverage = async () => {
    if (!cov.business_name || !cov.contact_name || !cov.email) return toast.error("Business name, contact name, and email are required");
    if (f.lat == null || f.lng == null) return toast.error("Enter a valid business address first");
    setCovLoading(true);
    try {
      await api.post("/public/coverage-request", { ...cov, latitude: f.lat, longitude: f.lng });
      setCovSent(true);
      toast.success("Thanks! We'll reach out about bringing Velopaths to your area.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCovLoading(false); }
  };

  useEffect(() => {
    api.get("/public/advertiser-pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  // Geocode the typed address → GPS (debounced). Drives territory matching + rider-map placement.
  useEffect(() => {
    const { address, city, state, zip } = f;
    if (!address.trim() || !city.trim() || !state.trim()) {
      setF((s) => (s.lat == null && s.lng == null ? s : { ...s, lat: null, lng: null }));
      return;
    }
    setLocating(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/geocode", { params: { address, city, state, zip } });
        if (data.found) setF((s) => ({ ...s, lat: data.lat, lng: data.lng }));
        else setF((s) => ({ ...s, lat: null, lng: null }));
      } catch { setF((s) => ({ ...s, lat: null, lng: null })); }
      finally { setLocating(false); }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.address, f.city, f.state, f.zip]);

  // Resolve the leasing partner + territory from the geocoded location.
  useEffect(() => {
    if (f.lat == null || f.lng == null) { setTerritory(null); return; }
    setResolving(true);
    const t = setTimeout(() => {
      api.get("/public/resolve-territory", { params: { lat: f.lat, lng: f.lng } })
        .then(({ data }) => setTerritory(data))
        .catch(() => setTerritory({ found: false }))
        .finally(() => setResolving(false));
    }, 300);
    return () => clearTimeout(t);
  }, [f.lat, f.lng]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.address.trim() || !f.city.trim() || !f.state.trim()) return toast.error("Please enter your business street address, city, and state");
    if (f.lat == null || f.lng == null) return toast.error("We couldn't locate that address — please check the street, city, state, and ZIP.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/advertiser", {
        business_name: f.business_name, name: f.name, email: f.email, password: f.password,
        address: f.address, city: f.city, state: f.state, zip: f.zip,
        latitude: f.lat, longitude: f.lng, plan: f.plan,
      });
      setSession(data.token, data.user);
      toast.success("Account created! Add your payment method to continue.");
      try {
        const { data: res } = await api.post("/advertiser/setup-checkout", { origin_url: window.location.origin });
        window.location.href = res.checkout_url;
        return;
      } catch (e) {
        toast.error("Could not open card setup. You can add a payment method from the Subscription tab.");
        navigate("/advertiser");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center justify-center mb-6">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex"><img src="/velopaths-logo.png" alt="Velopaths" className="h-8 w-auto" /></div>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="flex items-center gap-2 mb-1"><Megaphone className="w-5 h-5 text-orange-400" /><h1 className="font-heading text-2xl font-bold">Advertise your business</h1></div>
          <p className="text-sm text-muted-foreground mb-6">Enter your business address — we'll automatically match you to the Velopaths partner whose riders pass your door.</p>
          {pricing.promo?.active && (
            <div className="mb-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center gap-3" data-testid="signup-promo-banner">
              <Sparkles className="w-5 h-5 text-emerald-400 shrink-0" />
              <div className="text-sm">
                <span className="font-heading font-bold text-emerald-300">Limited-time: ${Number(pricing.promo.price) % 1 === 0 ? pricing.promo.price : Number(pricing.promo.price).toFixed(2)} first year</span>
                <span className="text-muted-foreground"> on the annual plan{pricing.promo.end ? `, ends ${new Date(pricing.promo.end).toLocaleDateString()}` : ""}. Renews at ${Number(pricing.annual) % 1 === 0 ? pricing.annual : Number(pricing.annual).toFixed(2)}/year.</span>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Business name</Label>
              <Input value={f.business_name} onChange={set("business_name")} required data-testid="advertiser-signup-business-name" className="mt-1.5" />
            </div>
            <div>
              <Label>Business street address</Label>
              <AddressAutocomplete value={f.address}
                onChange={(v) => setF((s) => ({ ...s, address: v, lat: null, lng: null }))}
                onSelect={(sg) => setF((s) => ({ ...s, address: sg.address || s.address, city: sg.city || s.city, state: US_STATES.includes(sg.state) ? sg.state : s.state, zip: sg.zip || s.zip, lat: sg.lat, lng: sg.lng }))}
                placeholder="Start typing your address…" testId="advertiser-signup-address-input" className="mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1">Pick your address from the list — your partner shop and territory are assigned automatically.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input value={f.city} onChange={set("city")} required placeholder="Austin" data-testid="advertiser-signup-city-input" className="mt-1.5" />
              </div>
              <div>
                <Label>State</Label>
                <Select value={f.state} onValueChange={(v) => setF((s) => ({ ...s, state: v }))}>
                  <SelectTrigger data-testid="advertiser-signup-state-select" className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {US_STATES.map((st) => <SelectItem key={st} value={st} data-testid={`advertiser-signup-state-${st}`}>{st}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>ZIP code</Label>
                <Input value={f.zip} onChange={set("zip")} placeholder="78701" data-testid="advertiser-signup-zip-input" className="mt-1.5" />
              </div>
            </div>

            {locating ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="advertiser-locating"><Loader2 className="w-4 h-4 animate-spin" />Locating your address…</div>
            ) : f.address.trim() && f.city.trim() && f.state.trim() && f.lat == null ? (
              <div className="text-sm text-amber-400" data-testid="advertiser-geocode-failed">We couldn't pinpoint that address yet — double-check the street, city, state, and ZIP.</div>
            ) : null}

            {/* Read-only, geography-driven partner attribution */}
            {f.lat != null && (
              resolving ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="territory-resolving"><Loader2 className="w-4 h-4 animate-spin" />Finding your territory…</div>
              ) : territory?.found ? (
                <div className="rounded-xl border border-primary/30 bg-primary/10 p-4" data-testid="territory-summary">
                  <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-primary" /><span className="font-heading font-bold text-sm">Your Velopaths territory</span></div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" /><span className="text-muted-foreground">Zone:</span> <span className="font-medium" data-testid="territory-zone-name">{territory.zone_name}</span></div>
                    <div className="flex items-center gap-2"><RouteIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" /><span className="text-muted-foreground">Bike path:</span> <span className="font-medium" data-testid="territory-bikepath-name">{territory.bike_path_name || "—"}</span></div>
                    <div className="flex items-center gap-2"><Store className="w-3.5 h-3.5 text-muted-foreground shrink-0" /><span className="text-muted-foreground">Partner shop:</span> <span className="font-medium" data-testid="territory-partner-name">{territory.partner_shop_name}</span></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">Assigned automatically from your address. Revenue sharing goes to this partner.</p>
                </div>
              ) : (
                <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 p-4" data-testid="territory-outside">
                  <div className="flex items-start gap-2">
                    <Store className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-heading font-bold text-sky-300">Managed directly by Velopaths</div>
                      <p className="text-xs text-muted-foreground mt-0.5">This location isn't inside an existing Velopaths zone — that's okay, you can still sign up. Your listing will be managed directly by Velopaths and reviewed by our team. Prefer a local partner? Let us know below and we'll work on bringing one to your area.</p>
                    </div>
                  </div>
                  {covSent ? (
                    <div className="mt-3 flex items-center gap-2 text-sm text-emerald-300" data-testid="coverage-request-sent"><CheckCircle2 className="w-4 h-4" />Request received — we'll be in touch about coverage in your area.</div>
                  ) : !coverageOpen ? (
                    <Button type="button" variant="outline" className="mt-3" onClick={openCoverage} data-testid="coverage-request-open">Request Velopaths coverage</Button>
                  ) : (
                    <div className="mt-3 space-y-2.5 border-t border-sky-500/30 pt-3" data-testid="coverage-request-form">
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        <div><Label className="text-xs">Business name</Label><Input value={cov.business_name} onChange={setC("business_name")} data-testid="coverage-business-name" className="mt-1" /></div>
                        <div><Label className="text-xs">Contact name</Label><Input value={cov.contact_name} onChange={setC("contact_name")} data-testid="coverage-contact-name" className="mt-1" /></div>
                        <div><Label className="text-xs">Email</Label><Input type="email" value={cov.email} onChange={setC("email")} data-testid="coverage-email" className="mt-1" /></div>
                        <div><Label className="text-xs">Phone <span className="text-muted-foreground">(optional)</span></Label><Input value={cov.phone} onChange={setC("phone")} data-testid="coverage-phone" className="mt-1" /></div>
                      </div>
                      <div><Label className="text-xs">Note <span className="text-muted-foreground">(optional)</span></Label><Textarea value={cov.note} onChange={setC("note")} rows={2} data-testid="coverage-note" className="mt-1" /></div>
                      <Button type="button" onClick={submitCoverage} disabled={covLoading} data-testid="coverage-request-submit">{covLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Submit coverage request</Button>
                    </div>
                  )}
                </div>
              )
            )}
            <div className="border-t border-border pt-4">
              <Label>Your name</Label>
              <Input value={f.name} onChange={set("name")} required data-testid="advertiser-signup-owner-name" className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={f.email} onChange={set("email")} required data-testid="advertiser-signup-email" className="mt-1.5" />
              </div>
              <div>
                <Label>Create Password</Label>
                <Input type="password" value={f.password} onChange={set("password")} required data-testid="advertiser-signup-password" className="mt-1.5" />
              </div>
            </div>
            <div className="rounded-xl border border-primary/20 bg-primary/10 p-4 space-y-3" data-testid="advertiser-pricing-info">
              <div>
                <h4 className="font-heading font-bold text-sm mb-2">Founding Advertiser plan</h4>
                <div className="grid grid-cols-1 gap-2">
                  {(() => {
                    const promoActive = pricing.promo?.active;
                    const annualPrice = Number(pricing.annual);
                    const fmt = (n) => `$${Number(n) % 1 === 0 ? n : Number(n).toFixed(2)}`;
                    const cards = [
                      { k: "advertiser_annual", price: fmt(promoActive ? Number(pricing.promo.price) : annualPrice), per: "/year per location", sub: promoActive ? `First year, then ${fmt(annualPrice)}/yr` : "Billed yearly per location", badge: promoActive ? "Limited-time" : "Founding", strike: promoActive ? fmt(annualPrice) : null },
                    ];
                    return cards.map(({ k, price, per, sub, badge, note, strike }) => (
                    <button key={k} type="button" onClick={() => setF({ ...f, plan: k })} data-testid={`signup-plan-${k}`}
                      className={`relative rounded-lg border p-3 text-left transition-colors ${f.plan === k ? "border-primary bg-primary/15" : "border-border hover:border-primary/40"}`}>
                      {badge && <span className={`absolute -top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${badge === "Limited-time" ? "bg-emerald-500 text-white" : "bg-primary text-primary-foreground"}`} data-testid="annual-savings-badge">{badge}</span>}
                      <div className="font-heading font-bold">{strike && <span className="text-xs text-muted-foreground font-normal line-through mr-1">{strike}</span>}{price}<span className="text-xs text-muted-foreground font-normal">{per}</span></div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}{note ? ` · ${note}` : ""}</div>
                    </button>
                    ));
                  })()}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Your participation includes one advertiser location, visibility in the Velopaths Rider App, map placement within an eligible zone, and one year of participation. Your card is saved now — you're only charged once your partner approves your listing.</p>
              </div>
              <div>
                <h4 className="font-heading font-bold text-sm">About 2.5¢ per rider (example)</h4>
                <p className="text-xs text-muted-foreground mt-1">If 4,000 riders use VeloPaths in your region this year, your $99/year advertising cost is roughly two and a half cents per rider — for placement directly on the routes they ride.</p>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading || resolving || locating} data-testid="advertiser-signup-submit-button">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create account
            </Button>
          </form>
          <p className="mt-5 text-sm text-center text-muted-foreground">Already registered? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
